/**
 * CategoryIcon — Lucide icon inside a colored circle wrapper
 *
 * Used everywhere transaction categories are displayed:
 * zone cards, transaction rows, detail sheets, review queue.
 */

import { Brush, CircleDollarSign, Receipt, CircleHelp, User } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { T } from '@/styles/tokens';

type CategoryKey = 'ART_SUPPLY' | 'BUSINESS_EXPENSE' | 'ART_INCOME' | 'UNCERTAIN' | 'PERSONAL' | 'IGNORE';

interface CategoryIconProps {
  category: string;
  size?: number;
  iconSize?: number;
  muted?: boolean;
}

const CATEGORY_CONFIG: Record<CategoryKey, {
  icon: LucideIcon;
  color: string;
  bg: string;
}> = {
  ART_SUPPLY: {
    icon: Brush,
    color: T.primary,
    bg: T.primaryGlow,
  },
  ART_INCOME: {
    icon: CircleDollarSign,
    color: T.green,
    bg: `rgba(45,127,62,0.07)`,
  },
  BUSINESS_EXPENSE: {
    icon: Receipt,
    color: T.purple,
    bg: `rgba(99,102,241,0.07)`,
  },
  PERSONAL: {
    icon: User,
    color: T.gray500,
    bg: `rgba(107,114,128,0.07)`,
  },
  UNCERTAIN: {
    icon: CircleHelp,
    color: T.warning,
    bg: 'rgba(224,122,61,0.12)',
  },
  IGNORE: {
    icon: User,
    color: T.gray400,
    bg: `rgba(156,163,175,0.07)`,
  },
};

/** Color lookup for external consumers (e.g. ActivityZones aggregate color) */
export const CATEGORY_COLORS: Record<string, string> = {
  ART_SUPPLY: T.primary,
  ART_INCOME: T.green,
  BUSINESS_EXPENSE: T.purple,
  PERSONAL: T.gray500,
  UNCERTAIN: T.warning,
  IGNORE: T.gray400,
};

export function CategoryIcon({ category, size = 28, iconSize, muted = false }: CategoryIconProps) {
  const config = CATEGORY_CONFIG[category as CategoryKey] || CATEGORY_CONFIG.UNCERTAIN;
  const Icon = config.icon;
  const iSize = iconSize || Math.round(size * 0.58);

  const bg = muted ? 'rgba(26,26,26,0.06)' : config.bg;
  const color = muted ? T.textSecondary : config.color;

  return (
    <div style={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}>
      <Icon size={iSize} color={color} />
    </div>
  );
}

export default CategoryIcon;
